import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaUser, FaEnvelope, FaLock, FaBuilding, FaHashtag } from "react-icons/fa";
import { Link } from "react-router-dom";
import AuthForm from "../components/AuthForm";
import InputField from "../components/InputField";
import Checkbox from "../components/Checkbox";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { showToast } from "../components/ToastNotification";
import analytics from "../services/analytics";

const Register = () => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        companyName: "",
        fiscalNumber: "",
    });

    const [agreeTerms, setAgreeTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };


    const handleRegister = async () => {
        const {
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
            companyName,
            fiscalNumber,
        } = formData;

        if (!firstName || !lastName || !email || !password || (!showPassword && !confirmPassword) || !companyName || !fiscalNumber) {
            setError("Please fill all required fields.");
            return;
        }
        if (!showPassword && password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (!agreeTerms) {
            setError("You must agree to the Terms and Conditions.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to register.");
            }

            showToast("Signup successful! Check your email to confirm your account.");
            
            // Track successful signup
            analytics.trackSignup('email');
            analytics.trackFreeTrialStart();
            
            navigate("/"); // or stay on the same page
        } catch (err) {
            showToast(err.message, "error");
            analytics.trackFormSubmit('register', false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Spinner loading={loading} />}
            <AuthForm title="Create Your Account">
                {error && <p className="text-red-500 text-center">{error}</p>}

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField label="First Name *" name="firstName" value={formData.firstName} onChange={handleChange} icon={FaUser} />
                    <InputField label="Last Name *" name="lastName" value={formData.lastName} onChange={handleChange} icon={FaUser} />
                </div>

                {/* Login Info */}
                <InputField
                    label="Email Address *"
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    icon={FaEnvelope}
                />
                <div className="relative">
                    <InputField
                        label="Password *"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password (minimum 8 characters)"
                        value={formData.password}
                        onChange={handleChange}
                        icon={FaLock}
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-11 text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>

                {!showPassword && (
                    <InputField
                        label="Confirm Password *"
                        name="confirmPassword"
                        type="password"
                        placeholder="Re-enter your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        icon={FaLock}
                    />
                )}

                {/* Company Info */}
                <InputField label="Company Name *" name="companyName" value={formData.companyName} onChange={handleChange} icon={FaBuilding} />
                <InputField label="Fiscal Number *" name="fiscalNumber" value={formData.fiscalNumber} onChange={handleChange} icon={FaHashtag} />

                <Checkbox
                    label="I agree to the Terms and Conditions and Privacy Policy"
                    checked={agreeTerms}
                    onChange={() => setAgreeTerms(!agreeTerms)}
                />
                <Button text="Sign Up" fullWidth onClick={handleRegister} />
                <p className="mt-4 text-center">
                    Already have an account? <Link to="/auth/signin" className="text-blue-600">Log in here</Link>
                </p>
            </AuthForm>
        </>
    );
};

export default Register;
